/**
 * Surface property getters and registration - NON-HOOK VERSION FOR POTREE
 *
 * This file contains only framework-agnostic code (no React hooks, no Apollo).
 * It can be safely imported by potree_fork without pulling in React dependencies.
 *
 * For React hook versions, see SurfaceProperties.ts
 */

import { PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService } from '../../reactive/globalPropertyService';
import { SurfaceKind } from 'shared-types';

// ============================================================================
// TYPES
// ============================================================================

type Coordinate = { x: number; y: number; z: number };

// ============================================================================
// PROPERTY REGISTRATION
// ============================================================================

/**
 * Register surface properties in the registry
 * Call this once at app initialization
 */
export function registerSurfaceProperties() {
  if (!propertyRegistry.isRegistered('surface_plane_position')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'surface_plane_position',
      name: 'Surface Plane Position',
      valueType: PropertyValueType.COORDINATE,
      defaultValue: { x: 0, y: 0, z: 0 },
      annotationTypes: [],
      description: 'Position of the surface plane',
    });
  }

  if (!propertyRegistry.isRegistered('surface_plane_normal')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'surface_plane_normal',
      name: 'Surface Plane Normal',
      valueType: PropertyValueType.COORDINATE,
      defaultValue: { x: 0, y: 0, z: 1 },
      annotationTypes: [],
      description: 'Normal vector of the surface plane',
    });
  }

  if (!propertyRegistry.isRegistered('surface_tolerance')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'surface_tolerance',
      name: 'Surface Tolerance',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0.1,
      annotationTypes: [],
      description: 'Tolerance value for surface detection',
      validation: { min: 0, max: 1, step: 0.01 },
      ui: { customUnit: 'm' },
    });
  }

  if (!propertyRegistry.isRegistered('surface_plane_rotation')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'surface_plane_rotation',
      name: 'Surface Plane Rotation',
      valueType: PropertyValueType.FLOAT,
      defaultValue: 0,
      annotationTypes: [],
      description: 'Rotation angle around the surface normal (in radians)',
      validation: { min: -Math.PI, max: Math.PI, step: 0.01 },
    });
  }

  if (!propertyRegistry.isRegistered('surface_kind')) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier: 'surface_kind',
      name: 'Surface Type',
      valueType: PropertyValueType.SELECT,
      defaultValue: SurfaceKind.SURFACE,
      annotationTypes: [],
      description: 'Type of surface: regular surface or cross-section',
      validation: {
        options: [
          { value: SurfaceKind.SURFACE, label: 'Surface' },
          { value: SurfaceKind.SECTION, label: 'Section' },
        ],
      },
    });
  }
}

// ============================================================================
// NON-HOOK HELPERS FOR POTREE (uses global property service)
// ============================================================================

/**
 * Get reactive property for surface plane position (non-hook version for Potree)
 * Uses the global property service, so it can be called from anywhere
 */
export function getSurfacePlanePositionProperty(surfaceId: string): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty('surface_plane_position', surfaceId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}

/**
 * Get reactive property for surface plane normal (non-hook version for Potree)
 */
export function getSurfacePlaneNormalProperty(surfaceId: string): ReactiveProperty<Coordinate> {
  const service = getGlobalPropertyService();
  return service.getProperty('surface_plane_normal', surfaceId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<Coordinate>;
}

/**
 * Get reactive property for surface tolerance (non-hook version for Potree)
 */
export function getSurfaceToleranceProperty(surfaceId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('surface_tolerance', surfaceId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}

/**
 * Get reactive property for surface plane rotation (non-hook version for Potree)
 * Rotation is in radians around the surface normal
 */
export function getSurfacePlaneRotationProperty(surfaceId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty('surface_plane_rotation', surfaceId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}

/**
 * Get reactive property for surface kind (non-hook version for Potree)
 * Returns SURFACE or SECTION
 */
export function getSurfaceKindProperty(surfaceId: string): ReactiveProperty<SurfaceKind> {
  const service = getGlobalPropertyService();
  return service.getProperty('surface_kind', surfaceId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<SurfaceKind>;
}

// Register properties immediately when module loads
registerSurfaceProperties();
